import React, { useState, useEffect, useRef } from "react";
import { HiArrowRight, HiSearch } from "react-icons/hi";
import {
  MdLocalShipping,
  MdCheckCircle,
  MdPending,
  MdCancel,
  MdInventory,
  MdPerson,
  MdPhone,
  MdLocationOn,
  MdReceipt,
  MdContentCopy,
} from "react-icons/md";
import { FaLeaf, FaSeedling, FaBoxOpen, FaTruck } from "react-icons/fa";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";

// ─── useInView ─────────────────────────────────────────────────────────────────
const useInView = (ref, threshold = 0.08) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

// ─── Dummy Order Data ──────────────────────────────────────────────────────────
const dummyOrders = {
  "RPA-20240512-001": {
    id: "RPA-20240512-001",
    status: "dikirim",
    tanggal: "12 Mei 2024",
    estimasi: "14–15 Mei 2024",
    user: {
      nama: "Budi Santoso",
      email: "budi.santoso@email.com",
      telepon: "+62 812-3456-7890",
      alamat: "Jl. Merdeka No. 45, Banda Aceh, Aceh 23111",
    },
    items: [
      { nama: "Tempe Koro Original", unit: "250 gram", qty: 3, harga: 15000 },
      { nama: "Tempe Koro Bumbu Rempah", unit: "250 gram", qty: 2, harga: 20000 },
    ],
    ekspedisi: "JNE REG",
    noResi: "JNE0012345678912",
    catatan: "Tolong dibungkus rapi ya kak, buat hadiah.",
  },
  "RPA-20240510-002": {
    id: "RPA-20240510-002",
    status: "selesai",
    tanggal: "10 Mei 2024",
    estimasi: "-",
    user: {
      nama: "Siti Aminah",
      email: "siti.aminah@email.com",
      telepon: "+62 857-9876-5432",
      alamat: "Jl. Sudirman No. 12, Lhokseumawe, Aceh 24351",
    },
    items: [
      { nama: "Tempe Koro Premium", unit: "300 gram", qty: 5, harga: 28000 },
    ],
    ekspedisi: "SiCepat BEST",
    noResi: "SCP9988776655443",
    catatan: "",
  },
  "RPA-20240508-003": {
    id: "RPA-20240508-003",
    status: "diproses",
    tanggal: "8 Mei 2024",
    estimasi: "13–14 Mei 2024",
    user: {
      nama: "Ahmad Fauzi",
      email: "ahmad.fauzi@email.com",
      telepon: "+62 823-1111-2222",
      alamat: "Jl. Pahlawan No. 7, Meulaboh, Aceh Barat 23681",
    },
    items: [
      { nama: "Paket Hemat Tempe Koro", unit: "5 × 250 gram", qty: 2, harga: 65000 },
      { nama: "Tempe Koro Kering", unit: "200 gram", qty: 1, harga: 35000 },
    ],
    ekspedisi: "J&T EXPRESS",
    noResi: "-",
    catatan: "",
  },
  "RPA-20240507-004": {
    id: "RPA-20240507-004",
    status: "dibatalkan",
    tanggal: "7 Mei 2024",
    estimasi: "-",
    user: {
      nama: "Dewi Rahayu",
      email: "dewi.rahayu@email.com",
      telepon: "+62 818-5555-6666",
      alamat: "Jl. Gatot Subroto No. 99, Langsa, Aceh 24451",
    },
    items: [
      { nama: "Tempe Koro Mini Snack", unit: "150 gram", qty: 4, harga: 18000 },
    ],
    ekspedisi: "-",
    noResi: "-",
    catatan: "Pembatalan atas permintaan pembeli.",
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const formatPrice = (price) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(price);

const STATUS_CONFIG = {
  diproses: {
    label: "Sedang Diproses",
    icon: MdPending,
    color: "#f59e0b",
    bg: "#fef3c7",
    border: "#fde68a",
    step: 1,
  },
  dikirim: {
    label: "Dalam Pengiriman",
    icon: MdLocalShipping,
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    step: 2,
  },
  selesai: {
    label: "Pesanan Selesai",
    icon: MdCheckCircle,
    color: "#22c55e",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    step: 3,
  },
  dibatalkan: {
    label: "Dibatalkan",
    icon: MdCancel,
    color: "#ef4444",
    bg: "#fef2f2",
    border: "#fecaca",
    step: 0,
  },
};

const STEPS = [
  { key: "diproses", label: "Pesanan Diterima", icon: MdReceipt },
  { key: "diproses", label: "Sedang Dikemas", icon: FaBoxOpen },
  { key: "dikirim", label: "Dalam Pengiriman", icon: FaTruck },
  { key: "selesai", label: "Pesanan Tiba", icon: MdCheckCircle },
];

// ─── CekPesananSection ─────────────────────────────────────────────────────────
const CekPesananSection = () => {
  const ref = useRef(null);
  const resultRef = useRef(null);
  const inView = useInView(ref);
  const [loaded, setLoaded] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleCek = () => {
    const trimmed = orderId.trim().toUpperCase();
    if (!trimmed) { setError("Masukkan Order ID terlebih dahulu."); return; }
    setLoading(true);
    setError("");
    setOrder(null);

    // Simulate API call delay
    setTimeout(() => {
      const found = dummyOrders[trimmed];
      if (found) {
        setOrder(found);
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      } else {
        setError(`Order ID "${trimmed}" tidak ditemukan. Periksa kembali dan coba lagi.`);
      }
      setLoading(false);
    }, 1200);
  };

  const handleCopyResi = () => {
    if (!order?.noResi || order.noResi === "-") return;
    navigator.clipboard.writeText(order.noResi).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const statusCfg = order ? STATUS_CONFIG[order.status] : null;
  const totalHarga = order
    ? order.items.reduce((s, i) => s + i.harga * i.qty, 0)
    : 0;

  return (
    <section ref={ref} className="bg-white min-h-screen">
      {/* ── Hero Banner ── */}
      <div className="relative bg-[#1a4a35] overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-end pr-8 select-none pointer-events-none">
          <span className="font-serif text-[160px] font-bold text-white/[0.04] leading-none">PESANAN</span>
        </div>
        <div
          className="absolute left-0 top-0 h-full w-1 bg-[#c9862a]"
          style={{
            transform: loaded ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: "top",
            transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s",
          }}
        />
        <div className="max-w-4xl mx-auto px-8 md:px-12 py-16 relative z-10">
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.8s ease 0.3s",
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                Lacak Pesanan
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight mb-3">
              Cek Status <br />
              <span className="text-[#c9862a]">Pesanan Anda</span>
            </h1>
            <p className="text-white/60 text-sm max-w-md leading-relaxed">
              Masukkan Order ID yang tertera pada email konfirmasi pembelian Anda untuk melacak status dan detail pesanan.
            </p>
          </div>
        </div>
      </div>

      {/* ── Search Box ── */}
      <div className="bg-[#f7f3ec] border-b border-[#e8e0d0]">
        <div className="max-w-4xl mx-auto px-8 md:px-12 py-10">
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(16px)",
              transition: "all 0.7s ease 0.5s",
            }}
          >
            <label className="block text-xs font-bold uppercase tracking-[3px] text-[#6b6456] mb-3">
              Order ID
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <MdReceipt
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c9862a]"
                  size={18}
                />
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => { setOrderId(e.target.value); setError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleCek()}
                  placeholder="Contoh: RPA-20240512-001"
                  className="w-full pl-12 pr-4 py-4 border border-[#e8e0d0] bg-white text-[#1a1a18] text-sm font-medium outline-none focus:border-[#c9862a] transition-colors placeholder-[#c9a870]/50 tracking-wide"
                />
              </div>
              <button
                onClick={handleCek}
                disabled={loading}
                className="flex items-center justify-center gap-3 bg-[#c9862a] hover:bg-[#e09b3a] disabled:bg-[#c9862a]/60 text-[#1a1a18] font-bold text-sm tracking-widest uppercase px-8 py-4 transition-all duration-200 group whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#1a1a18]/30 border-t-[#1a1a18] rounded-full animate-spin" />
                    Mencari...
                  </>
                ) : (
                  <>
                    <HiSearch size={16} />
                    Cek Pesanan
                    <HiArrowRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-3 mt-4 px-4 py-3 bg-red-50 border border-red-200">
                <MdCancel className="text-red-500 mt-0.5 flex-shrink-0" size={16} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Hint */}
            <p className="text-[#a09880] text-xs mt-3 leading-relaxed">
              Order ID dapat ditemukan di email konfirmasi pembelian dengan format{" "}
              <span className="font-mono text-[#c9862a]">RPA-YYYYMMDD-XXX</span>. Coba:{" "}
              {Object.keys(dummyOrders).slice(0, 2).map((k, i) => (
                <span key={k}>
                  <button
                    onClick={() => { setOrderId(k); setError(""); }}
                    className="font-mono text-[#1a4a35] hover:text-[#c9862a] underline underline-offset-2 transition-colors"
                  >
                    {k}
                  </button>
                  {i < 1 && ", "}
                </span>
              ))}
            </p>
          </div>
        </div>
      </div>

      {/* ── Result ── */}
      <div ref={resultRef} className="max-w-4xl mx-auto px-8 md:px-12 py-12">
        {order && statusCfg && (
          <div
            style={{
              opacity: 1,
              animation: "fadeSlideUp 0.6s ease forwards",
            }}
          >
            {/* ── Status Header ── */}
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                Hasil Pencarian
              </span>
            </div>

            {/* Status Card */}
            <div
              className="p-6 border mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              style={{ backgroundColor: statusCfg.bg, borderColor: statusCfg.border }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: statusCfg.color + "20" }}
                >
                  <statusCfg.icon size={24} style={{ color: statusCfg.color }} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#6b6456] mb-0.5">
                    Status Pesanan
                  </p>
                  <p className="font-serif text-xl font-bold" style={{ color: statusCfg.color }}>
                    {statusCfg.label}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#a09880] mb-0.5">Order ID</p>
                <p className="font-mono font-bold text-[#1a1a18] text-sm">{order.id}</p>
                <p className="text-xs text-[#a09880] mt-1">Dipesan: {order.tanggal}</p>
              </div>
            </div>

            {/* ── Progress Tracker (only for non-cancelled) ── */}
            {order.status !== "dibatalkan" && (
              <div className="bg-white border border-[#e8e0d0] p-6 mb-6">
                <p className="text-xs font-bold uppercase tracking-[3px] text-[#6b6456] mb-6">
                  Progres Pesanan
                </p>
                <div className="relative">
                  {/* Track line */}
                  <div className="absolute top-5 left-5 right-5 h-[2px] bg-[#e8e0d0]" />
                  <div
                    className="absolute top-5 left-5 h-[2px] bg-[#c9862a] transition-all duration-700"
                    style={{
                      width:
                        statusCfg.step === 1
                          ? "0%"
                          : statusCfg.step === 2
                          ? "50%"
                          : "100%",
                    }}
                  />
                  <div className="relative grid grid-cols-4 gap-2">
                    {STEPS.map((step, i) => {
                      const done = statusCfg.step > i;
                      const active = statusCfg.step === i + 1;
                      const Icon = step.icon;
                      return (
                        <div key={i} className="flex flex-col items-center gap-2">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 relative z-10"
                            style={{
                              backgroundColor: done || active ? "#c9862a" : "#f7f3ec",
                              borderColor: done || active ? "#c9862a" : "#e8e0d0",
                            }}
                          >
                            <Icon
                              size={16}
                              style={{ color: done || active ? "#fff" : "#a09880" }}
                            />
                          </div>
                          <span
                            className="text-[10px] font-bold text-center leading-tight"
                            style={{ color: done || active ? "#1a4a35" : "#a09880" }}
                          >
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* ── Informasi Pengiriman ── */}
              <div className="bg-white border border-[#e8e0d0] p-6">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#e8e0d0]">
                  <MdLocalShipping className="text-[#c9862a]" size={18} />
                  <p className="text-xs font-bold uppercase tracking-[3px] text-[#6b6456]">
                    Info Pengiriman
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-1">Ekspedisi</p>
                    <p className="font-bold text-[#1a1a18] text-sm">{order.ekspedisi}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-1">No. Resi</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold text-[#1a4a35] text-sm">
                        {order.noResi}
                      </p>
                      {order.noResi !== "-" && (
                        <button
                          onClick={handleCopyResi}
                          className="flex items-center gap-1 text-[10px] px-2 py-1 border border-[#e8e0d0] text-[#6b6456] hover:border-[#c9862a] hover:text-[#c9862a] transition-colors"
                        >
                          <MdContentCopy size={11} />
                          {copied ? "Tersalin!" : "Salin"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-1">
                      Estimasi Tiba
                    </p>
                    <p className="font-bold text-[#1a1a18] text-sm">{order.estimasi}</p>
                  </div>
                  {order.catatan && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-1">
                        Catatan
                      </p>
                      <p className="text-[#6b6456] text-sm italic">"{order.catatan}"</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Informasi Pembeli ── */}
              <div className="bg-white border border-[#e8e0d0] p-6">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#e8e0d0]">
                  <MdPerson className="text-[#c9862a]" size={18} />
                  <p className="text-xs font-bold uppercase tracking-[3px] text-[#6b6456]">
                    Informasi Pembeli
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MdPerson className="text-[#c9862a] mt-0.5 flex-shrink-0" size={16} />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-0.5">Nama</p>
                      <p className="font-bold text-[#1a1a18] text-sm">{order.user.nama}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MdPhone className="text-[#c9862a] mt-0.5 flex-shrink-0" size={16} />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-0.5">Telepon</p>
                      <p className="font-bold text-[#1a1a18] text-sm">{order.user.telepon}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MdLocationOn className="text-[#c9862a] mt-0.5 flex-shrink-0" size={16} />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#a09880] mb-0.5">Alamat</p>
                      <p className="text-[#3a3a34] text-sm leading-relaxed">{order.user.alamat}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Detail Produk ── */}
            <div className="bg-white border border-[#e8e0d0] p-6 mb-6">
              <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#e8e0d0]">
                <MdInventory className="text-[#c9862a]" size={18} />
                <p className="text-xs font-bold uppercase tracking-[3px] text-[#6b6456]">
                  Detail Produk
                </p>
              </div>

              <div className="space-y-3 mb-5">
                {order.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-3 border-b border-[#e8e0d0] last:border-0 gap-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-[#f7f3ec] border border-[#e8e0d0] flex items-center justify-center flex-shrink-0">
                        <FaSeedling className="text-[#1a4a35] text-xs" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-[#1a1a18] text-sm truncate">{item.nama}</p>
                        <p className="text-[#a09880] text-xs">{item.unit}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-[#a09880] mb-0.5">
                        {item.qty} × {formatPrice(item.harga)}
                      </p>
                      <p className="font-serif font-bold text-[#1a4a35] text-sm">
                        {formatPrice(item.harga * item.qty)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex items-center justify-between pt-3 border-t-2 border-[#1a4a35]">
                <span className="text-sm font-bold text-[#3a3a34] uppercase tracking-wide">
                  Total Pembayaran
                </span>
                <span className="font-serif text-2xl font-bold text-[#1a4a35]">
                  {formatPrice(totalHarga)}
                </span>
              </div>
            </div>

            {/* ── Bottom Note ── */}
            <div className="p-5 bg-[#f7f3ec] border border-[#e8e0d0] flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <FaLeaf className="text-[#1a4a35] text-xl flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[#3a3a34] text-sm font-bold">Butuh bantuan?</p>
                <p className="text-[#6b6456] text-xs mt-0.5 leading-relaxed">
                  Hubungi tim Rumoh Pangan Aceh melalui WhatsApp atau email jika ada kendala dengan pesanan Anda.
                </p>
              </div>
              <a
                href="https://wa.me/628123456789"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-[#c9862a] text-[#1a1a18] text-xs font-bold tracking-widest uppercase px-5 py-2.5 hover:bg-[#c9862a] hover:text-white transition-all duration-200 group flex-shrink-0"
              >
                Hubungi Kami
                <HiArrowRight
                  size={12}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </div>
          </div>
        )}

        {/* ── Empty state ── */}
        {!order && !loading && !error && (
          <div
            className="py-20 text-center"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.7s ease 0.3s",
            }}
          >
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="h-px w-16 bg-[#e8e0d0]" />
              <FaBoxOpen className="text-[#e8e0d0] text-5xl" />
              <div className="h-px w-16 bg-[#e8e0d0]" />
            </div>
            <p className="font-serif text-xl font-bold text-[#3a3a34] mb-2">
              Masukkan Order ID
            </p>
            <p className="text-[#a09880] text-sm max-w-sm mx-auto leading-relaxed">
              Gunakan kolom pencarian di atas untuk melacak status dan detail pesanan Anda.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const CekPesanan = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <CekPesananSection />
    <Footer />
  </main>
);

export default CekPesanan;